// THE SHELL'S DEFAULT KEYBOARD MAP (HOME_AMBIENT_SKIN_SPEC §5 "Bindings").
// Armed once per shell by functions/shortcuts.js, on the first key or listing.
//
//   General    ⌘K search (ADOPTED — HeaderSearch owns it) · / prompt bar ·
//              ? this sheet · Esc close / leave edit mode
//   Go to      G then H · T · C · M → home · tasks · calendar · chat
//   Dashboard  1–9 the nth app tile · J / K next / previous widget ·
//              Enter open the focused widget (ADOPTED — the card's own
//              onKeyDown) · A the focused widget's square action (the frame's
//              HomeWidgetAction, pressed through its own onClick —
//              functions/shortcutsHome.js homeWidgetKeyAction) · E edit the board
//
// Deliberately NOT bound:
//   • ⌘⇧L — the spec rules it out.
//
// ADOPTION, not double-binding:
//   • ⌘K — `HeaderSearch` (shared) declares its own document keydown per
//     mounted instance and calls preventDefault. The registry LISTS the row and
//     never runs it: a second handler for one keystroke is exactly the double
//     bind the spec forbids, and listener order cannot make a registry run safe
//     (the header re-mounts on canvas routes, so its listener can land after
//     the shell's).
//   • Enter — the focused app tile's own `onKeyDown` (pages/main.js `_widget`)
//     opens it; the registry lists the row only.
//   • Esc — every overlay owns its own Escape (AppDialog stops it at the root,
//     the dropdown / modal roots close their layer); the guard keeps the
//     registry out while any is open. With no overlay, an open app window is
//     the top thing: Esc closes it through the window's own X
//     (closeAppWindow); only then does Esc reach edit mode. The customize drawer still carries its own Escape
//     too (pages/main.js); both write the SAME exit patch and the registry
//     yields to a handled key, so one Esc is one write in either listener
//     order (docs/patches/shortcuts-esc-precedence.patch retires the drawer's
//     copy so the precedence lives in one place).
//
// Every `when` / `run` receives (shellRoot element, root state[, event]).
// Group labels and row labels are bundle keys; the `shell.shortcuts.*` ones
// ship with docs/patches/shortcuts-i18n.patch.

export function registerDefaultShortcuts() {
  const el = this
  const reg = (def) => el.call('registerShortcut', def)
  const member = (h, s) => !!(s && s.signedIn)
  const GENERAL = 'shell.shortcuts.group.general'
  const GO_TO = 'shell.shortcuts.group.goTo'
  const BOARD = 'shell.headerTabs.dashboard'
  // The exit every customize control writes (functions/homeCustomizeDismiss.js
  // CUSTOMIZE_EXIT_PATCH — the drawer's ×, Done, Escape and outside press).
  const leaveEditMode = (root) => {
    root.update({ _homeCustomizeMode: false, _homeCustomizeDrawerTucked: false })
  }

  // ── General ────────────────────────────────────────────────────────────
  reg({
    id: 'search',
    keys: 'mod+k',
    label: 'common.action.search',
    group: GENERAL,
    groupOrder: 1,
    order: 1,
    adopted: 'HeaderSearch'
  })
  reg({
    id: 'composer',
    keys: '/',
    label: 'shell.shortcuts.row.composer',
    group: GENERAL,
    groupOrder: 1,
    order: 2,
    when: member,
    run: (h) => h.call('focusShellComposer')
  })
  reg({
    id: 'sheet',
    keys: '?',
    label: 'shell.shortcuts.row.sheet',
    group: GENERAL,
    groupOrder: 1,
    order: 3,
    when: member,
    run: (h) => h.call('openShortcutsSheet')
  })
  reg({
    id: 'escape',
    keys: 'escape',
    label: 'shell.shortcuts.row.escape',
    group: GENERAL,
    groupOrder: 1,
    order: 4,
    // The TOP thing first: an open app window (PageSlot) sits over the board,
    // so Esc closes it — through the window's own X (closeAppWindow below) —
    // before it ever reaches edit mode on the board beneath.
    when: (h, root) =>
      !!(root && (root._canvasSlot || root._canvasSlotSecond || root._homeCustomizeMode)),
    run: (h, root, e) => {
      if (root._canvasSlot || root._canvasSlotSecond) return h.call('closeAppWindow', e)
      leaveEditMode(root)
    }
  })

  // ── Go to — the router, the way the header's Home button navigates ─────
  const go = (id, letter, path, label, order) =>
    reg({
      id,
      seq: ['g', letter],
      label,
      group: GO_TO,
      groupOrder: 2,
      order,
      when: member,
      run: (h) => {
        // Home, like the header's home icon: a fresh Fly in Fly mode
        // (functions/assistantFlyWorkspace.js assistantFlyHomeFresh).
        if (path === '/' && h.context?.functions?.assistantFlyHomeFresh) {
          h.call('assistantFlyHomeFresh')
          return
        }
        h.router(path, h.getRoot())
      }
    })
  // The workspace or its CLI (functions/workspaceMode.js): Ctrl+` (⌘` is the
  // system's on a Mac). Under the Agent frame its own key bridge takes it.
  reg({
    id: 'workspaceModeToggle',
    keys: 'mod+`',
    label: 'workspace.agentMode.toggle',
    group: GENERAL,
    groupOrder: 1,
    order: 9,
    when: member,
    run: (h) => h.call('workspaceModeToggle')
  })
  go('goHome', 'h', '/', 'shell.app.home', 1)
  go('goTasks', 't', '/tasks', 'shell.app.tasks', 2)
  go('goCalendar', 'c', '/calendar', 'shell.app.calendar', 3)
  go('goChat', 'm', '/chat', 'shell.app.chat', 4)

  // ── Dashboard — the home board, when it is the surface ─────────────────
  const onBoard = (h, root) => member(h, root) && !root._homeCustomizeMode
  reg({
    id: 'openApp',
    keys: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    keycaps: ['1', '9'],
    joiner: 'range',
    label: 'shell.shortcuts.row.openApp',
    group: BOARD,
    groupOrder: 3,
    order: 1,
    when: onBoard,
    run: (h, root, e) =>
      e && /^[1-9]$/.test(e.key) ? h.call('openHomeTile', Number(e.key), e) : false
  })
  reg({
    id: 'nextWidget',
    keys: 'j',
    repeat: true,
    label: 'shell.shortcuts.row.nextWidget',
    group: BOARD,
    groupOrder: 3,
    order: 2,
    when: onBoard,
    run: (h, root, e) => h.call('focusHomeCard', 1, e)
  })
  reg({
    id: 'prevWidget',
    keys: 'k',
    repeat: true,
    label: 'shell.shortcuts.row.prevWidget',
    group: BOARD,
    groupOrder: 3,
    order: 3,
    when: onBoard,
    run: (h, root, e) => h.call('focusHomeCard', -1, e)
  })
  reg({
    id: 'openWidget',
    keys: 'enter',
    label: 'shell.shortcuts.row.openWidget',
    group: BOARD,
    groupOrder: 3,
    order: 4,
    adopted: 'the focused card’s own button (components/HomeWidgetFrame.js title / app tile)'
  })
  // A — the focused widget's square action. Live only while a card holds the
  // focus, so the ⌘K palette (focus in its own field) never offers a command
  // that has no card to act on; the ? sheet lists it all the same.
  reg({
    id: 'widgetAction',
    keys: 'a',
    label: 'shell.shortcuts.row.widgetAction',
    group: BOARD,
    groupOrder: 3,
    order: 5,
    when: (h, root) => onBoard(h, root) && !!h.call('homeCardOfTarget'),
    run: (h, root, e) => h.call('homeWidgetKeyAction', e)
  })
  reg({
    id: 'editBoard',
    keys: 'e',
    label: 'shell.accountMenu.customizeDashboard',
    group: BOARD,
    groupOrder: 3,
    order: 6,
    when: (h, root) => member(h, root) && !!h.call('homeBoardActive'),
    run: (h, root) => {
      // The two enter paths that exist (the board's context menu Customize and
      // the account menu's) write exactly this; the exit is the drawer's.
      if (root._homeCustomizeMode) leaveEditMode(root)
      else root.update({ _homeCustomizeMode: true }, { preventFetch: true })
    }
  })
  el.call('registerHomeResizeShortcuts')
}

// `/` — the shell's prompt bar (app.js `CanvasPromptTextarea.Textarea`, the
// bottom pill on the home). Returns false — the key stays the browser's —
// where the composer is not on screen (routes with their own prompt hide it).
export function focusShellComposer() {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  const composer = host && host.CanvasPromptTextarea
  const field = composer && composer.Textarea
  const node = field && field.node
  if (!node || typeof node.focus !== 'function' || node.isConnected === false) return false
  if (typeof node.getClientRects === 'function' && !node.getClientRects().length) return false
  try {
    node.focus({ preventScroll: true })
  } catch (_) {
    node.focus()
  }
  const doc = node.ownerDocument
  return !doc || doc.activeElement === node
}

// Esc over an app window — press the window's OWN X (canvas/components/
// PageSlot.js, `Header.CloseBtn` of each pane): its click is the one close a
// window has — the immersive member's landing, the rail, the split promotion
// (closing the primary hands its frame to the second pane), the fullscreen
// reset and the ?page= URL sync all live there, so the key and the X cannot
// drift (the 1–9 keys open a tile the same way, through the tile's own click).
// The pane that holds focus closes (the split pane when you are in it), else
// the primary. A palette pick has no event: the X gets a stub it can
// preventDefault / stopPropagation. Returns false — the key stays the
// browser's — when no window's X is on screen.
export function closeAppWindow(event) {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  const slot = host && host.PageSlot
  if (!slot) return false
  const st = host.state
  const root = (st && st.root) || st || {}
  const t = event && event.target
  const holds = (pane) =>
    !!(pane && pane.node && t && typeof pane.node.contains === 'function' && pane.node.contains(t))
  const pane =
    (root._canvasSlotSecond && holds(slot.PaneSecond) && slot.PaneSecond) ||
    (root._canvasSlot ? slot.PanePrimary : slot.PaneSecond)
  const btn = pane && pane.Header && pane.Header.CloseBtn
  if (!btn || typeof btn.onClick !== 'function') return false
  const ev = event || { preventDefault() {}, stopPropagation() {} }
  btn.onClick(ev, btn, btn.state)
  return true
}

// `?` — opens the sheet (components/ShortcutsOverlay.js) through its AppDialog
// state key. The sheet mounts at the shell root (docs/patches/
// shortcuts-overlay-mount.patch), behind the shell's INTERACTIVE stage; until
// it has rendered, the key stays unbound.
export function openShortcutsSheet() {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  if (!host || !host.ShortcutsOverlay || !host.ShortcutsOverlay.node) return false
  const st = host.state
  const root = (st && st.root) || st
  if (!root || typeof root.update !== 'function') return false
  root.update({ _shortcutsSheetOpen: true })
  return true
}
